import React from 'react';
import { useApp } from '../context/AppContext';
import { Check, Calendar, MessageSquare, ListTodo } from 'lucide-react';
import { format, isToday, isTomorrow, isPast } from 'date-fns';

const TaskItem = ({ task }) => {
  const { toggleTaskComplete, setSelectedTaskId, lists, tags } = useApp();

  const taskList = lists.find(l => l.id === task.listId);
  const taskTags = tags.filter(t => task.tagIds?.includes(t.id));
  const completedSubtasks = task.subtasks?.filter(s => s.completed).length || 0;
  const totalSubtasks = task.subtasks?.length || 0;

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'MMM d');
  };

  const getDueDateColor = (dateStr) => {
    if (!dateStr) return 'text-gray-500 dark:text-gray-400';
    const date = new Date(dateStr);
    if (isPast(date) && !isToday(date)) return 'text-red-500';
    if (isToday(date)) return 'text-blue-600 dark:text-blue-400';
    return 'text-gray-500 dark:text-gray-400';
  };

  return (
    <div
      onClick={() => setSelectedTaskId(task.id)}
      className={`group flex items-start gap-3 p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-sm cursor-pointer transition-all duration-200
        ${task.completed ? 'opacity-60' : ''}`}
    >
      {/* Checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleTaskComplete(task.id);
        }}
        className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200
          ${task.completed
            ? 'bg-blue-600 border-blue-600'
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 group-hover:border-blue-400'
          }`}
      >
        {task.completed && <Check className="w-3 h-3 text-white" />}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium leading-tight break-words
            ${task.completed ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
            {task.title}
          </p>
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-2 sm:gap-3 mt-1.5 flex-wrap">
          {/* Tags - show only first 2 on mobile */}
          {taskTags.length > 0 && (
            <div className="flex items-center gap-1">
              {taskTags.slice(0, 2).map(tag => (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
                  style={{
                    backgroundColor: `${tag.color}20`,
                    color: tag.color,
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full hidden sm:block"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span className="truncate max-w-[60px] sm:max-w-none">{tag.name}</span>
                </span>
              ))}
              {taskTags.length > 2 && (
                <span className="text-xs text-gray-400 dark:text-gray-500">+{taskTags.length - 2}</span>
              )}
            </div>
          )}

          {/* Due date */}
          {task.dueDate && (
            <span className={`flex items-center gap-1 text-xs ${getDueDateColor(task.dueDate)}`}>
              <Calendar className="w-3 h-3" />
              {formatDueDate(task.dueDate)}
            </span>
          )}

          {/* Subtasks count */}
          {totalSubtasks > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
              <ListTodo className="w-3 h-3" />
              {completedSubtasks}/{totalSubtasks}
            </span>
          )}

          {/* Notes indicator */}
          {task.notes && (
            <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hidden sm:flex">
              <MessageSquare className="w-3 h-3" />
            </span>
          )}

          {/* List name - hidden on small screens */}
          {taskList && (
            <span className="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline truncate max-w-[80px]">
              {taskList.name}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskItem;
